import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronDown,
  ChevronLeft,
  Video as VideoIcon,
  FolderOpen,
  MonitorPlay,
  Search,
  Loader2,
} from 'lucide-react';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { HierarchicalFolder, FolderDetails } from '@/types/hierarchicalFolders';

export interface VideoFolderPickerItem {
  id: string;
  title: string;
  thumbnailUrl?: string;
}

interface VideoFolderPickerProps {
  value?: string | null;
  /** Lista opcional (lista plana exibida na raiz). Se omitida, usa os vídeos processados do store. */
  videos?: VideoFolderPickerItem[];
  onChange: (videoId: string) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
}

/**
 * Seletor global de vídeos com busca + navegação nas pastas reais do usuário.
 *
 * As pastas vêm do sistema hierárquico (o mesmo do dashboard), carregadas via
 * `hierarchicalFoldersService` em estado LOCAL — não usamos `fetchFolderDetails`
 * do store porque ele muta o `currentFolder` global e bagunçaria o dashboard.
 * Suporta subpastas em N níveis (pilha de navegação). Quando a prop `videos`
 * restringe a lista (ex.: Rastreamento), os vídeos dentro das pastas também são
 * interseccionados com ela.
 */
export function VideoFolderPicker({
  value,
  videos: videosProp,
  onChange,
  placeholder,
  className,
  triggerClassName,
  disabled,
}: VideoFolderPickerProps) {
  useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Pilha de navegação de pastas (suporta subpastas em N níveis).
  const [folderStack, setFolderStack] = useState<string[]>([]);

  // Pastas carregadas via service com estado local (sem efeitos colaterais no store global).
  const [rootFolders, setRootFolders] = useState<HierarchicalFolder[]>([]);
  const [rootLoading, setRootLoading] = useState(false);
  const [detailsCache, setDetailsCache] = useState<Record<string, FolderDetails>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);

  const storeVideos = useVideoDashboardStore((s) => s.videos);

  const videos: VideoFolderPickerItem[] = useMemo(() => {
    if (videosProp) return videosProp;
    return storeVideos
      .filter((v) => v.status === 'processed' || (v as any).status === 'active' || !v.status)
      .map((v) => ({ id: v.id, title: v.title, thumbnailUrl: v.thumbnailUrl }));
  }, [videosProp, storeVideos]);

  const drillFolderId = folderStack[folderStack.length - 1] ?? null;

  // Carrega as pastas raiz quando o popover abre.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRootLoading(true);
    hierarchicalFoldersService
      .listRoot()
      .then((r) => { if (!cancelled) setRootFolders(r.items); })
      .catch(() => { if (!cancelled) setRootFolders([]); })
      .finally(() => { if (!cancelled) setRootLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Carrega subpastas + vídeos ao entrar numa pasta (com cache por id).
  useEffect(() => {
    if (!drillFolderId || detailsCache[drillFolderId]) return;
    let cancelled = false;
    setDetailsLoading(true);
    hierarchicalFoldersService
      .getFolderDetails(drillFolderId)
      .then((d) => { if (!cancelled) setDetailsCache((p) => ({ ...p, [drillFolderId]: d })); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDetailsLoading(false); });
    return () => { cancelled = true; };
  }, [drillFolderId, detailsCache]);

  // Dentro de um Dialog (modal), o react-remove-scroll bloqueia o wheel deste
  // Popover (Portal, fora do modal) e a lista não rola. Anexamos um listener
  // não-passivo que aplica o scroll manualmente. Usamos CALLBACK REF (e não
  // useEffect+useRef) porque o conteúdo do Popover monta via Portal depois do
  // efeito disparar — o ref ficaria null e o listener nunca seria adicionado.
  const wheelCleanup = useRef<(() => void) | null>(null);
  const listRef = useCallback((el: HTMLDivElement | null) => {
    wheelCleanup.current?.();
    wheelCleanup.current = null;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollHeight <= el.clientHeight) return;
      e.preventDefault();
      // deltaMode: 0=pixels, 1=linhas, 2=páginas — normaliza tudo p/ pixels,
      // senão mouses que reportam linhas (alguns no Firefox) rolam quase nada.
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      el.scrollTop += e.deltaY * factor;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanup.current = () => el.removeEventListener('wheel', onWheel);
  }, []);

  const selected = videos.find((v) => v.id === value);
  const q = search.trim().toLowerCase();

  const allowedIds = useMemo(
    () => (videosProp ? new Set(videosProp.map((v) => v.id)) : null),
    [videosProp]
  );

  const filteredVideos = useMemo(
    () => videos.filter((v) => v.title.toLowerCase().includes(q)),
    [videos, q]
  );
  const filteredRootFolders = useMemo(
    () => rootFolders.filter((f) => f.name.toLowerCase().includes(q)),
    [rootFolders, q]
  );

  const currentDetails = drillFolderId ? detailsCache[drillFolderId] : null;
  const drillSubfolders = useMemo(
    () => (currentDetails?.children ?? []).filter((f) => f.name.toLowerCase().includes(q)),
    [currentDetails, q]
  );
  const drillVideos = useMemo(() => {
    if (!currentDetails) return [];
    return currentDetails.videos
      .filter((v) => v.title.toLowerCase().includes(q))
      .filter((v) => !allowedIds || allowedIds.has(v.id))
      .map((v) => ({ id: v.id, title: v.title, thumbnailUrl: v.thumbnailUrl }));
  }, [currentDetails, q, allowedIds]);

  const enterFolder = (id: string) => {
    setFolderStack((s) => [...s, id]);
    setSearch('');
  };
  const goBack = () => {
    setFolderStack((s) => s.slice(0, -1));
    setSearch('');
  };

  const close = () => {
    setOpen(false);
    setSearch('');
    setFolderStack([]);
  };

  const videoButton = (v: VideoFolderPickerItem) => (
    <button
      key={v.id}
      type="button"
      onClick={() => {
        onChange(v.id);
        close();
      }}
      className={`w-full text-left text-[12px] rounded-lg mx-0.5 my-0.5 px-2 py-1.5 flex items-center gap-2.5 transition-colors ${
        value === v.id ? 'bg-[#4B7BFF] text-white' : 'text-foreground hover:bg-muted'
      }`}
    >
      <VideoIcon
        className={`w-3.5 h-3.5 shrink-0 ${value === v.id ? 'text-white' : 'text-muted-foreground'}`}
      />
      <span className="truncate">{v.title}</span>
    </button>
  );

  const folderButton = (f: HierarchicalFolder) => (
    <button
      key={f.id}
      type="button"
      onClick={() => enterFolder(f.id)}
      className="w-full text-left text-[12px] rounded-lg mx-0.5 my-0.5 px-2 py-1.5 flex items-center gap-2.5 text-foreground hover:bg-muted transition-colors"
    >
      <FolderOpen className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate flex-1">{f.name}</span>
      <ChevronDown className="w-3 h-3 -rotate-90 shrink-0 text-muted-foreground" />
    </button>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setSearch('');
          setFolderStack([]);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={
            triggerClassName ??
            `flex h-10 min-w-[200px] w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-sm text-foreground hover:border-[#4B7BFF] transition-colors disabled:opacity-50 ${className ?? ''}`
          }
        >
          <span className="flex items-center gap-2 min-w-0">
            <MonitorPlay className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <span className={`truncate ${selected ? '' : 'text-muted-foreground'}`}>
              {selected ? selected.title : placeholder ?? translateInterfaceText("Selecione um vídeo")}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] overflow-hidden"
      >
        <div className="h-[2px] bg-gradient-to-r from-[#4B7BFF]/60 via-[#4B7BFF]/20 to-transparent" />
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={drillFolderId ? translateInterfaceText("Pesquisar nesta pasta...") : translateInterfaceText("Pesquisar vídeo ou pasta...")}
              className="h-8 pl-8 text-[12px] bg-muted/40 border-border rounded-lg focus-visible:ring-1 focus-visible:ring-[#4B7BFF]/30"
            />
          </div>
        </div>

        <div ref={listRef} className="max-h-[min(55vh,290px)] overflow-y-auto overscroll-contain p-1">
          {drillFolderId ? (
            <>
              <button
                type="button"
                onClick={goBack}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>{translateInterfaceText("Voltar")}</span>
              </button>
              <div className="flex items-center gap-2 px-3 py-1.5">
                <FolderOpen className="w-3.5 h-3.5 text-[#4B7BFF]" />
                <span className="text-[12px] font-semibold text-foreground truncate">
                  {currentDetails?.name ?? translateInterfaceText("Pasta")}
                </span>
              </div>
              <div className="mx-3 my-1 h-px bg-border" />

              {detailsLoading && !currentDetails ? (
                <div className="px-3 py-4 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {translateInterfaceText("Carregando...")}</div>
              ) : (
                <>
                  {drillSubfolders.length > 0 && (
                    <>
                      <p className="px-3 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {translateInterfaceText("Subpastas")}</p>
                      {drillSubfolders.map(folderButton)}
                    </>
                  )}

                  <p className="px-3 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {translateInterfaceText("Vídeos")}</p>
                  {drillVideos.length === 0 ? (
                    <p className="px-3 py-3 text-[12px] text-center text-muted-foreground">
                      {translateInterfaceText("Nenhum vídeo nesta pasta")}</p>
                  ) : (
                    drillVideos.map(videoButton)
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {filteredVideos.length > 0 && (
                <>
                  <p className="px-3 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {translateInterfaceText("Vídeos")}</p>
                  {filteredVideos.map(videoButton)}
                </>
              )}

              {rootLoading ? (
                <div className="px-3 py-3 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {translateInterfaceText("Carregando pastas...")}</div>
              ) : filteredRootFolders.length > 0 ? (
                <>
                  <div className="mx-3 my-1 h-px bg-border" />
                  <p className="px-3 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {translateInterfaceText("Pastas")}</p>
                  {filteredRootFolders.map(folderButton)}
                </>
              ) : null}

              {!rootLoading && filteredVideos.length === 0 && filteredRootFolders.length === 0 && (
                <p className="px-3 py-4 text-[12px] text-center text-muted-foreground">
                  {translateInterfaceText("Nenhum resultado")}</p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
